import { useEffect, useRef, useState, type FormEvent } from "react";
import { searchPlacesWithMeta } from "../search";
import type { ItineraryStop, PlaceCandidate } from "../types";
import { Icon } from "./Icon";

interface PlaceSearchProps {
  destination: string;
  onAdd: (place: PlaceCandidate) => void;
  onSaveIdea?: (place: PlaceCandidate) => void;
  onClose: () => void;
  initialQuery?: string;
  contextStops?: ItineraryStop[];
  freeSlotMinutes?: number;
}

function importedCoordinates(value: string): { lat: number; lon: number } | null {
  const match = value.match(/(?:@|q=|query=)?(-?\d{1,2}(?:\.\d+)?)[,\s]+(-?\d{1,3}(?:\.\d+)?)/i);
  if (!match) return null;
  const lat = Number(match[1]);
  const lon = Number(match[2]);
  return Number.isFinite(lat) && lat >= -90 && lat <= 90 && Number.isFinite(lon) && lon >= -180 && lon <= 180 ? { lat, lon } : null;
}

function straightLineKm(place: PlaceCandidate, stop: ItineraryStop): number {
  const radians = (degrees: number) => degrees * Math.PI / 180;
  const deltaLat = radians(stop.lat - place.lat);
  const deltaLon = radians(stop.lon - place.lon);
  const left = radians(place.lat);
  const right = radians(stop.lat);
  const value = Math.sin(deltaLat / 2) ** 2 + Math.cos(left) * Math.cos(right) * Math.sin(deltaLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

export function PlaceSearch({ destination, onAdd, onSaveIdea, onClose, initialQuery = "", contextStops = [], freeSlotMinutes }: PlaceSearchProps) {
  const [query, setQuery] = useState(initialQuery);
  const [importValue, setImportValue] = useState("");
  const [results, setResults] = useState<PlaceCandidate[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState("");
  const [worldwide, setWorldwide] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    setStatus("loading");
    setMessage("");
    setResults([]);
    try {
      const result = await searchPlacesWithMeta(query, abort.signal, { destination, worldwide });
      setResults(result.places);
      setStatus("done");
      setMessage(result.places.length
        ? `${result.places.length} places found${result.cached ? result.stale ? " from the last available search" : " from cache" : ""}.`
        : "No matches. Try a place plus city or country.");
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Place search failed.");
    }
  }

  function add(place: PlaceCandidate) {
    onAdd(place);
    setQuery("");
    setResults([]);
    setStatus("idle");
    setMessage("");
    onClose();
  }

  function importPlace(event: FormEvent) {
    event.preventDefault();
    const coordinates = importedCoordinates(importValue);
    if (!coordinates) {
      setStatus("error");
      setMessage("Paste coordinates such as 35.0116, 135.7681 or a map URL containing @latitude,longitude.");
      return;
    }
    add({ id: `manual:${coordinates.lat},${coordinates.lon}`, name: "Imported map point", displayName: `Imported coordinates ${coordinates.lat}, ${coordinates.lon}`, ...coordinates, category: "place", type: "coordinates" });
  }

  return (
    <section className="place-search" aria-labelledby="place-search-title">
      <div className="search-heading">
        <div><h2 id="place-search-title">Find a place</h2><p>Results are biased to {destination} unless you search worldwide.</p></div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close place search"><Icon name="x" /></button>
      </div>
      <form className="search-form" onSubmit={submit} role="search">
        <Icon name="search" />
        <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Place in ${destination}`} aria-label="Search for a place" minLength={3} maxLength={120} required />
        <button type="submit" className="button button-dark" disabled={status === "loading"}>{status === "loading" ? "Searching…" : "Search"}</button>
      </form>
      <label className="search-scope">
        <input type="checkbox" checked={worldwide} onChange={(event) => setWorldwide(event.target.checked)} />
        <span>Search worldwide</span>
      </label>
      <form className="coordinate-import" onSubmit={importPlace}>
        <input value={importValue} onChange={(event) => setImportValue(event.target.value)} placeholder="Paste coordinates or a map URL" aria-label="Paste coordinates or a map URL" />
        <button type="submit" className="button button-secondary button-small">Import point</button>
      </form>
      <a className="search-attribution" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">Place data © OpenStreetMap contributors</a>
      <p className={`search-status ${status === "error" ? "is-error" : ""}`} role="status">{message}</p>
      {results.length ? (
        <div className="search-results">
          {results.map((place) => (
            <article key={place.id} className="search-result">
              <span className="result-pin"><Icon name="map" size={17} /></span>
              <span><strong>{place.name}</strong><small>{place.displayName}</small>{contextStops.length ? (() => { const distance = Math.min(...contextStops.map((stop) => straightLineKm(place, stop))); const minutes = Math.max(1, Math.ceil(distance / 4.5 * 60)); return <small className="search-context">{distance < 3 ? "Same area" : "Detour"} · about {minutes} min walking from the nearest stop (straight-line estimate){freeSlotMinutes !== undefined ? ` · ${minutes + 60 <= freeSlotMinutes ? "fits" : "does not fit"} the ${freeSlotMinutes} min free block` : ""}</small>; })() : null}</span>
              <div className="search-result-actions"><button type="button" className="button button-secondary button-small" onClick={() => add(place)}><Icon name="plus" size={15} /> Add</button>{onSaveIdea ? <button type="button" className="button button-quiet button-small" onClick={() => onSaveIdea(place)}>Save idea</button> : null}</div>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
